import React, { useState, useEffect } from "react";
import PageHeadline from "../../components/shared/PageHeadline";
import { useTranslation } from "react-i18next";
import { useFetchEventHistory, useUserProfile } from "../../hooks/useApi";
import { isAuthenticated } from "../../lib/api";
import Profile, { ProfileData } from "./Profile";

export default function Index(): React.ReactElement {
    const { t } = useTranslation();
    const [isClient, setIsClient] = useState(false);
    
    // Check authentication
    useEffect(() => {
        setIsClient(true);
        const isAuth = isAuthenticated();
        if (!isAuth) {
            window.location.href = "/login";
        }
    }, []);

    // Fetch user profile data from API
    const { data: userProfile, isLoading, error } = useUserProfile();
    const { data: eventHistoryData, isLoading: isEventHistoryLoading, error: eventHistoryError } = useFetchEventHistory();

    // Transform API data to ProfileData format
    const transformProfileData = (apiData: typeof userProfile): ProfileData | undefined => {
        if (!apiData?.data) return undefined;
        
        return {
            uusrId: apiData.data.uusrId,
            uusrName: apiData.data.uusrName,
            uusrEmail: apiData.data.uusrEmail,
            uusrPhoneNumber: apiData.data.uusrPhoneNumber,
            uusrGender: apiData.data.uusrGender,
            uusrBloodType: apiData.data.uusrBloodType,
            uusrNationality: apiData.data.uusrNationality,
            uusrIdentityType: apiData.data.uusrIdentityType,
            uusrIdentityNumber: apiData.data.uusrIdentityNumber,
            uusrDateOfBirth: apiData.data.uusrDateOfBirth,
            uusrOccupation: apiData.data.uusrOccupation,
            uusrCommunity: apiData.data.uusrCommunity,
        };
    };

    // Use API data if available, otherwise fallback to profile-data.json for development
    const profileData: ProfileData | undefined = transformProfileData(userProfile);

    // Handle loading state for client-side rendering
    if (!isClient) {
        return (
            <div className="text-[#164481] md:px-0 px-4 bg-secondary min-h-screen font-wantCoffee">
                <div className="relative min-h-[240px] lg:min-h-[300px] bg-brandBlue top-[86px] md:top-[109px] 2xl:top-[136px] flex justify-center items-center font-wantCoffee">
                    <PageHeadline title={t("profile.title")} />
                </div>
                <div className="top-12 md:top-40 lg:top-60 relative bg-white 2xl:px-0 md:px-14 px-0 py-5 pb-96">
                    <div className="bg-white min-h-screen container mx-auto px-4 w-full font-poppins text-[#164481]">
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-pulse text-lg">Loading...</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="text-[#164481] md:px-0 px-4 bg-white min-h-screen">
            {/* Page Header with Banner Background */}
            <div className="relative min-h-[240px] lg:min-h-[300px] bg-primary top-[86px] md:top-[109px] 2xl:top-[136px] flex justify-center items-center">
                <PageHeadline title={t("profile.title")} />
            </div>

            {/* Main Content Area */}
            <div className="top-12 md:top-40 relative bg-white 2xl:px-0 md:px-14 px-0 py-5 pb-20 lg:pb-40">
                <div className="bg-white container mx-auto px-4 w-full font-poppins text-[#164481]">
                    {/* Profile Component */}
                    <Profile
                        data={profileData}
                        eventHistory={eventHistoryData}
                        isLoading={isLoading}
                        error={error instanceof Error ? error : null}
                        isEventHistoryLoading={isEventHistoryLoading}
                        eventHistoryError={eventHistoryError instanceof Error ? eventHistoryError : null}
                    />
                </div>
            </div>
        </div>
    );
}
